import { Howl, Howler } from 'howler';
import { decodeBase64ToBlob } from './audioPlatform';

Howler.autoSuspend = false;

const activeSounds = new Set();

const MIME_TO_FORMAT = {
	'audio/mpeg': 'mp3',
	'audio/mp3': 'mp3',
	'audio/mp4': 'mp4',
	'audio/x-m4a': 'm4a',
	'audio/m4a': 'm4a',
	'audio/ogg': 'oga',
	'audio/webm': 'webm',
	'audio/wav': 'wav',
	'audio/wave': 'wav',
	'audio/aac': 'aac',
	'audio/flac': 'flac',
};

const mimeToFormat = (mimeType = '') =>
	MIME_TO_FORMAT[mimeType.split(';')[0].trim()] ?? 'mp3';

const cleanupHowl = (howl) => {
	activeSounds.delete(howl);

	try {
		howl.unload();
	} catch {
		// already unloaded
	}
};

export const unlockHowlerAudio = () => {
	if (Howler.ctx && Howler.ctx.state === 'suspended') {
		Howler.ctx.resume().catch(() => {});
	}
};

export const playAudio = (src, { volume = 1, format } = {}) => {
	let resolve;
	let reject;

	const promise = new Promise((res, rej) => {
		resolve = res;
		reject = rej;
	});

	const howl = new Howl({
		src: [src],
		format: format ? [format] : undefined,
		html5: true,
		volume,
		preload: true,
		onend: () => {
			cleanupHowl(howl);
			resolve();
		},
		onerror: (_id, error) => {
			cleanupHowl(howl);
			reject(new Error(String(error ?? 'Playback error')));
		},
		onloaderror: (_id, error) => {
			cleanupHowl(howl);
			reject(new Error(String(error ?? 'Load error')));
		},
	});

	activeSounds.add(howl);
	howl.play();

	const stop = () => {
		if (!activeSounds.has(howl)) {
			return;
		}

		try {
			howl.stop();
		} catch {
			// ignore
		}

		cleanupHowl(howl);
		resolve();
	};

	return { promise, stop };
};

export const playAudioBlob = (blob, { volume = 1 } = {}) => {
	const objectUrl = URL.createObjectURL(blob);
	const format = mimeToFormat(blob.type);

	const revoke = () => {
		try {
			URL.revokeObjectURL(objectUrl);
		} catch {
			// ignore
		}
	};

	const handle = playAudio(objectUrl, { volume, format });
	const originalStop = handle.stop;

	return {
		promise: handle.promise.finally(revoke),
		stop: () => {
			revoke();
			originalStop();
		},
	};
};

export const playAudioBase64 = (base64, mimeType = 'audio/mpeg', { volume = 1 } = {}) => {
	const blob = decodeBase64ToBlob(base64, mimeType);
	return playAudioBlob(blob, { volume });
};

export const stopAllHowlerAudio = () => {
	activeSounds.forEach((howl) => {
		try {
			howl.stop();
			howl.unload();
		} catch {
			// ignore
		}
	});
	activeSounds.clear();
};
